import React from 'react'
import PropTypes from 'prop-types'
import { helpers, prismicHelpers } from '../../../helpers'
import { ThemeOverride } from '../../core'
import MobileMenuPage from './MobileMenuPage'

/**
* Mobile Menu Content Component.
*
* Renders the contents of the mobile menu.
* Content is separated into pages that are placed side-by-side horizontally
* based on the level they are on. Only one page is set to be visible per
* level. It is assumed that if a page is set to be visible, it's parent(s)
* is/are also set to be visible.
* Only one level and only one page on that level can be visible at any given time.
* if the level is increased or decreased, then the entire contents of the menu
* are shifted so that the current level is the one that is visible.
*
*/
class MobileMenuContent extends React.Component {
  constructor(props) {
    super(props)

    this.uid = helpers.generateID('mm')

    /*
    * `level` is for the currently visible level.
    * `visiblePages` contains the pageId for the visible page at each given level.
    */
    this.state = {
      level: 0,
      visiblePages: [props.content.id],
    }

    this.changeLevel = this.changeLevel.bind(this)
    this.renderHeaderContent = this.renderHeaderContent.bind(this)
  }

  /**
  * Updates the state of the visible page on the given level to be the specified
  * page.
  */
  changeLevel(level, pageId) {
    const currentPages = this.state.visiblePages

    currentPages[level] = pageId

    this.setState({
      level,
      visiblePages: currentPages,
    })
  }

  /**
  * Renders the header of the main page of the mobile menu.
  */
  renderHeaderContent() {
    const userIsLoggedIn = typeof __USER__ === 'object' && __USER__.loggedIn


    const userHasName = userIsLoggedIn && __USER__.name


    const welcomeMessage = (
      <h2>{ `Hello${userHasName ? `, ${__USER__.name}` : ''}.` }</h2>
    )


    const textColor = this.props.settings.textColor || 'light'

    let subMessage

    // Platinum users see the amount of money they have available in points.
    // Non-Platinum users see a link to learm more about Platinum Points.
    // Non-logged in users see a link to login.
    if (userIsLoggedIn) {
      if (__USER__.platinum && __USER__.points) {
        subMessage = (
          <a href="/account/platinum?ref=mm-plat-points">
            { `You have ${__USER__.points} in Platinum Points` }
          </a>
        )
      } else {
        subMessage = (
          <a href="/rewards/platinum?ref=mm-plat-learn">
            Get 10% back with Platinum Rewards <div><em>Learn More</em></div>
          </a>
        )
      }
    } else {
      subMessage = (
        <a href="/login?ref=mm-header">
          Sign in for the best experience.
        </a>
      )
    }

    return (
      <div
        id={this.uid}
        className={`mobile-menu-header has-${textColor}-text can-override-background-image can-override-background-color`}
      >
        <ThemeOverride
          selector={`#${this.uid}`}
          values={{
            backgroundColor: this.props.settings.backgroundColor ? `#${this.props.settings.backgroundColor}` : '',
            backgroundImage: this.props.settings.backgroundImage ? this.props.settings.backgroundImage.url : '',
          }}
          choices={{
            backgroundColor: Boolean(this.props.settings.backgroundColor),
            backgroundImage: Boolean(this.props.settings.backgroundImage),
          }}
        />
        { welcomeMessage }
        { subMessage }
      </div>
    )
  }

  render() {
    const content = this.props.content


    const translateAmount = this.state.level ? this.state.level * 100 : 0

    // Only show items that the user has access to.
    content.items = content.items
      .filter(child => prismicHelpers.userHasPermission(child))

    return (
      <nav
        className="mobile-menu-content"
        style={{
          transform: `translateX(-${translateAmount}%)`,
          WebkitTransform: `translateX(-${translateAmount}%)`,
        }}
        role="menubar"
        aria-label="Main Menu"
      >
        <MobileMenuPage
          content={content}
          changeLevel={this.changeLevel}
          menuState={this.state}
          toggleSidebar={this.props.toggleSidebar}
        >
          { this.renderHeaderContent() }
        </MobileMenuPage>
      </nav>
    )
  }
}

MobileMenuContent.defaultProps = {
  settings: {},
}

MobileMenuContent.propTypes = {
  content: PropTypes.object.isRequired,
  settings: PropTypes.object,
  toggleSidebar: PropTypes.func.isRequired,
}

export default MobileMenuContent
